import numeral from 'numeral'
import { ReactNode, useMemo } from 'react'

import { InternalLink } from 'src/components/atoms/Link'
import { Tooltip } from 'src/components/atoms/Tooltip'

import {
  GetAdminProjectQuery,
  GetProjectByUriQuery
} from 'src/config/generated/graphql'
import { carbonCalculatorPath } from 'src/config/paths'
import { ArrayElement } from 'src/types/common'

import {
  calculateCarbon,
  calculateArea,
  withDefaults
} from '../CarbonCalculator/helper'
import { CalculatorTab } from '../CarbonCalculator/types'

// Note: Figma spec uses `#C2D900` for the band bg; nearest palette token is
// `limeBright` (#C5D800). Stat illustrations live in `public/img/icons/` and
// are loaded via raw <img> since they're decorative brand artwork.
type ProjectStatsBandRebrandProps = {
  projectData:
    | ArrayElement<GetProjectByUriQuery['Project']>
    | ArrayElement<GetAdminProjectQuery['Project']>
}

type StatProps = {
  value: string
  unit?: string
  description: string
  iconSrc: string
  tooltip?: ReactNode
}

const Stat = ({ value, unit, description, iconSrc, tooltip }: StatProps) => (
  <div className='flex items-center gap-4'>
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img
      src={iconSrc}
      alt=''
      aria-hidden
      className='w-[72px] h-auto shrink-0'
    />
    <div className='flex flex-col gap-px'>
      <p className='font-display-rebrand font-extrabold text-[32px] leading-[1.2] text-forestDeep m-0'>
        {value}
        {unit ? <span className='ml-2'>{unit}</span> : null}
      </p>
      <div className='flex items-center gap-2'>
        <p className='font-body-rebrand text-base leading-[1.6] text-forestDeep/60 whitespace-nowrap m-0'>
          {description}
        </p>
        {tooltip && (
          <Tooltip
            overlay={
              <div className='px-4 py-[10px] max-w-[262px]'>{tooltip}</div>
            }
          >
            <span className='inline-flex items-center justify-center w-4 h-4 rounded-full bg-forestDeep text-limeBright text-[10px] font-display-rebrand font-bold leading-[1.2] cursor-help'>
              i
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  </div>
)

export const ProjectStatsBandRebrand = ({
  projectData
}: ProjectStatsBandRebrandProps) => {
  const totalTrees = useMemo(
    () => projectData.Applications_aggregate.aggregate?.sum?.Approved || 0,
    [projectData]
  )

  const area = useMemo(() => {
    const { formattedValue, unit } = calculateArea(totalTrees)
    return { value: formattedValue, unit }
  }, [totalTrees])

  const carbon = useMemo(
    () =>
      calculateCarbon(
        withDefaults({ formTab: CalculatorTab.Stems, stems: totalTrees })
      ),
    [totalTrees]
  )

  return (
    <div className='bg-limeBright w-full'>
      <div className='container mx-auto px-4 lg:px-20 py-8'>
        <div className='flex flex-col md:flex-row gap-8 lg:gap-12 items-start md:items-center justify-between'>
          <Stat
            value={numeral(totalTrees).format('0,0')}
            description='Trees from Trees That Count'
            iconSrc='/img/icons/rebranded/forest-forest-green.svg'
          />
          <Stat
            value={area.value}
            unit={area.unit}
            description='Expected to be restored'
            iconSrc='/img/icons/rebranded/land-forest-green.svg'
            tooltip={
              <p className='text-[13px] leading-5 font-medium'>
                {numeral(totalTrees).format('0,0')} trees received from Trees
                That Count will help to restore approx. {area.value} {area.unit}{' '}
                of land based on an average of 1.7 m plant spacing.{' '}
                <InternalLink
                  color='none'
                  className='text-inherit! underline'
                  href={carbonCalculatorPath}
                >
                  Learn more
                </InternalLink>
              </p>
            }
          />
          <Stat
            value={carbon.formattedValue}
            unit={carbon.unit}
            description='Expected to be removed'
            iconSrc='/img/icons/rebranded/carbon-forest-green.svg'
            tooltip={
              <p className='text-[13px] leading-5 font-medium'>
                {numeral(totalTrees).format('0,0')}{' '}
                {totalTrees === 1 ? 'tree' : 'trees'} will help to remove{' '}
                {carbon.formattedValue} tonnes of carbon dioxide from our
                atmosphere over 50 years of continuous plant growth.{' '}
                <InternalLink
                  color='none'
                  className='text-inherit! underline'
                  href={carbonCalculatorPath}
                >
                  Learn more
                </InternalLink>
              </p>
            }
          />
        </div>
      </div>
    </div>
  )
}
